import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import LanguageSelector from '@webclient/components/language-selector';
import LogoutButton from '@webclient/components/logout-button';
import { notification } from '@webclient/components/notification';
import FormProvider from '@webclient/components/react-hook-form/FormProvider';
import RHFInputField from '@webclient/components/react-hook-form/RHFInputField';
import FormHeader from '@webclient/components/settings/components/form-header';
import Avatar from '@webclient/components/settings/components/tabs/general/components/avatar';
import FieldGroup from '@webclient/components/settings/components/tabs/general/components/field-group';
import useMutateMe from '@webclient/components/settings/components/tabs/general/hooks/useMutateMe';
import userFormSchema from '@webclient/components/settings/components/tabs/general/schemas';
import UserAccountActionCard from '@webclient/components/settings/components/user-account-action-card';
import { StyledScrollableTabContent } from '@webclient/components/settings/styles';
import ThemeMode from '@webclient/components/theme-mode';

import { signUp } from '@webclient/lib/auth/fingerprint';
import { setSession } from '@webclient/lib/jwt';

import useAppSelector from '@webclient/hooks/useAppSelector';
import useDeleteUser from '@webclient/hooks/useDeleteUser';
import useGetCredit from '@webclient/hooks/useGetCredit';
import useCreateRefreshToken from '@webclient/hooks/useGetRefreshToken';
import useResponsive from '@webclient/hooks/useResponsive';
import useUpdateMe from '@webclient/hooks/useUpdateMe';
import useWorkspace from '@webclient/hooks/useWorkspace';

import ArrowLeftIcon from '~icons/knowz-iconify/arrow-left';
import EditRoundedIcon from '~icons/knowz-iconify/edit-rounded';

import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import paths from '@webclient/router/paths';
import { useConfirm } from 'material-ui-confirm';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';

type Props = {
  onClose: (event: React.SyntheticEvent) => void;
};

const SAVE_DEBOUNCE = 3000;

export default function General({ onClose }: Props) {
  const navigateTo = useNavigate();
  const { resetWorkspaceToIndividualRole, setupMode } = useWorkspace();
  const { handleUpdateMe } = useUpdateMe();
  const me = useAppSelector((state) => state.auth.me);
  const { t } = useTranslate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isItDesktop = useResponsive('up', 'md');
  const { getAccountType } = useGetCredit();
  const confirm = useConfirm();
  const { mutateAsync: mutateMeAsync, isPending: isMutateMePending } =
    useMutateMe({
      onError: () => {
        notification.error({
          message: t(
            'component.settings.general.updateProfile.response.error',
            'An error occurred while updating your profile',
          ),
        });
      },
      onSuccess: () => {
        notification.success({
          message: t(
            'component.settings.general.updateProfile.response.success',
            'Your profile has been updated',
          ),
        });
      },
    });

  const { mutateCreateRefreshTokenAsync } = useCreateRefreshToken(
    (variables) => {
      const token = variables.data.token;
      setSession(token);
      handleUpdateMe(token);
    },
  );

  const { mutateAsync: mutateDeleteUserAsync, isPending: isDeleteUserPending } =
    useDeleteUser({
      onSuccess: () => {
        notification.success({
          message: t(
            'response.success.deleteAccount',
            'Your account has been deleted successfully.',
          ),
        });
        handleLogout();
      },
      onError: () => {
        notification.error({
          message: t(
            'response.errors.deleteAccount',
            'Unable to delete your account. Please try again later.',
          ),
        });
      },
    });

  const methods = useForm({
    resolver: yupResolver(userFormSchema(t)),
    defaultValues: {
      firstName: me?.first_name || '',
      lastName: me?.last_name || '',
    },
  });

  const {
    getValues,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    watch,
    reset,
  } = methods;

  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  const persistUpdate = useCallback(async () => {
    if (!isDirty || !isValid) return;

    if (isMutateMePending) {
      saveTimeout.current && clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(persistUpdate, SAVE_DEBOUNCE);
    } else {
      const data = getValues();
      await mutateMeAsync({
        firstName: data.firstName,
        lastName: data.lastName,
      });

      await mutateCreateRefreshTokenAsync();
      reset(data);
    }
  }, [
    isDirty,
    isValid,
    isMutateMePending,
    getValues,
    mutateMeAsync,
    mutateCreateRefreshTokenAsync,
    reset,
  ]);

  useEffect(() => {
    const subscription = watch(() => {
      handleSubmit(() => ({}))();
      saveTimeout.current && clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(persistUpdate, SAVE_DEBOUNCE);
    });

    saveTimeout.current && clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(persistUpdate, SAVE_DEBOUNCE);

    return () => subscription.unsubscribe();
  }, [handleSubmit, persistUpdate, watch]);

  async function handleLogout() {
    onClose();
    window.localStorage.removeItem('token');
    resetWorkspaceToIndividualRole();

    if (setupMode !== null) {
      navigateTo(paths.home.pathname);
    }

    if (!executeRecaptcha) return;
    const recaptchaToken = await executeRecaptcha();
    const response = await signUp(recaptchaToken);

    if (response?.data.status === 'Ok') {
      window.localStorage.removeItem('fingerPrintId');
      handleUpdateMe(response.data.token);
    }
  }

  async function handleDeleteAccount() {
    try {
      await confirm({
        title: t('layout.auth.deleteAccount.title', 'Delete Account'),
        description: t(
          'layout.auth.deleteAccount.description',
          'Are you sure you want to delete your account? it is undoable and all your data will be lost.',
        ),
        confirmationText: t('layout.auth.deleteAccount.confirm', 'Delete'),
        cancellationText: t('layout.auth.deleteAccount.cancel', 'Cancel'),
      });
    } catch (error) {
      return;
    }
    await mutateDeleteUserAsync();
  }

  if (getAccountType() === 'visitor') {
    return (
      <>
        <FormHeader
          title={t('layout.main.sections.header.settings.title.freeAccount')}
        >
          {!isItDesktop && (
            <IconButton
              color="secondary"
              variant="ghost"
              onClick={onClose}
            >
              <ArrowLeftIcon />
            </IconButton>
          )}
        </FormHeader>

        <StyledScrollableTabContent>
          <UserAccountActionCard
            accountType="visitor"
            type="search"
          />

          <FieldGroup
            label={t(
              'layout.main.sections.header.settings.general.theme.title',
              'Theme',
            )}
          >
            <ThemeMode />
          </FieldGroup>

          <Divider />

          <FieldGroup
            label={t(
              'layout.main.sections.header.settings.general.language',
              'Language',
            )}
          >
            <LanguageSelector />
          </FieldGroup>
        </StyledScrollableTabContent>
      </>
    );
  }

  return (
    <>
      <FormHeader
        title={t(
          'layout.main.sections.header.settings.title.loggedIn',
          'General',
        )}
      >
        {!isItDesktop && (
          <IconButton
            color="secondary"
            variant="ghost"
            onClick={onClose}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
      </FormHeader>

      <StyledScrollableTabContent
        sx={{
          px: 6,
          py: 5,
        }}
      >
        {/* <Card>Add Credit Card Here</Card> */}
        <FieldGroup
          label={t(
            'layout.main.sections.header.settings.general.yourPhoto',
            'Your Photo',
          )}
        >
          <Stack
            sx={{ width: '100%' }}
            gap={3}
          >
            <Text
              variant="textSm"
              color="text.tertiary"
            >
              {t(
                'layout.main.sections.header.settings.general.updateProfile.avatar',
                'Visible to your teammates in workspaces',
              )}
            </Text>

            <Avatar />
          </Stack>
        </FieldGroup>

        <FormProvider methods={methods}>
          <Stack gap={4}>
            <FieldGroup
              isItDesktop={isItDesktop}
              label={t(
                'layout.main.sections.header.settings.general.firstName',
                'First Name',
              )}
            >
              <RHFInputField
                name="firstName"
                error={!!errors.firstName?.message}
                helperText={errors.firstName?.message}
                endAdornment={<EditRoundedIcon width={13} />}
              />
            </FieldGroup>

            <FieldGroup
              isItDesktop={isItDesktop}
              label={t(
                'layout.main.sections.header.settings.general.lastName',
                'Last Name',
              )}
            >
              <RHFInputField
                name="lastName"
                error={!!errors.lastName?.message}
                helperText={errors.lastName?.message}
                endAdornment={<EditRoundedIcon width={13} />}
              />
            </FieldGroup>
          </Stack>
        </FormProvider>

        <Divider />

        <FieldGroup
          label={t(
            'layout.main.sections.header.settings.general.theme',
            'Theme',
          )}
        >
          <ThemeMode />
        </FieldGroup>

        <Divider />

        <FieldGroup
          label={t(
            'layout.main.sections.header.settings.general.language',
            'Language',
          )}
        >
          <LanguageSelector />
        </FieldGroup>

        <Divider />

        <FieldGroup
          label={t('layout.auth.buttons.deleteAccount', 'Delete Account')}
        >
          <LoadingButton
            loading={isDeleteUserPending}
            variant="outlined"
            color="error"
            onClick={handleDeleteAccount}
          >
            {t('layout.auth.buttons.delete', 'Delete')}
          </LoadingButton>
        </FieldGroup>

        <Divider />

        <FieldGroup label={t('layout.auth.buttons.logout', 'Logout')}>
          <LogoutButton handleLogout={handleLogout} />
        </FieldGroup>
      </StyledScrollableTabContent>
    </>
  );
}
